import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {AmchartsService} from '../../shared/_services/amcharts.service';
import {takeUntil} from 'rxjs/operators';
import {AssetModel} from '../_models/asset.model';
import {PlantRiskCountryService} from '../_services/plant-risk-country.service';
import {PlantRiskCountryModel} from '../_models/plant-risk-country.model';
import {MercureService} from '../_services/mercure.service';
import {MercureModel} from '../_models/mercure.model';
import {DfService} from '../../shared/_services/df.service';

@Component({
  selector: 'app-market-chart',
  templateUrl: './market-chart.component.html',
  styleUrls: ['./market-chart.component.scss']
})

export class MarketChartComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() country: AssetModel;

  @ViewChild('mf_chartdiv') chartDiv: ElementRef;

  private prc: PlantRiskCountryModel;
  private mercure: MercureModel;
  private dataMerged: any[];

  private chart: am4charts.XYChart;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private amChartService: AmchartsService,
    private mercureService: MercureService,
    private plantRiskCountryService: PlantRiskCountryService,
    private ngZone: NgZone,
  ) {
  }

  ngOnInit() {
    this.getPrc();
    this.getMercure();
  }

  ngAfterViewInit() {
    this.updateChartData();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getPrc(): void {
    this.plantRiskCountryService.onNewPlantRiskCountry()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (prc) => {
          if (prc.length) {
            this.prc = prc.find((i) => i.country === this.country.label) || {country: this.country.label, prcData: []};
            this.updateChartData();
          }
        }
      );
  }

  private getMercure(): void {
    this.mercureService.onNewMercure()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (mercure) => {
          if (mercure.length) {
            this.mercure = mercure.find((i) => i.country === this.country.label) || {country: this.country.label, mercureData: []};
            this.updateChartData();
          }
        }
      );
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  private updateChartData(): void {
    this.createChart();
    if (this.chart && this.prc && this.mercure) {
      this.dataMerged = DfService.dfMerge([this.prc.prcData, this.mercure.mercureData], 'outer', 'ts');
      this.ngZone.runOutsideAngular(() => {
        this.chart.data = this.dataMerged;
        this.hideOrShowSeries('co2Fc');
        this.hideOrShowSeries('coalFc');
        this.hideOrShowSeries('gasFc');
      });
    }
  }

  private hideOrShowSeries(valueY: string): void {
    const s = this.chart.series.values.find((cs) => cs.dataFields.valueY === valueY);
    if (s) {
      const hasData = this.prc.prcData.some((m) => m[valueY] !== null);
      if (!hasData) {
        s.hide();
        s.hiddenInLegend = true;
      } else {
        // explicitly set to override possible previous hides
        s.show();
        s.hiddenInLegend = false;
      }
    }
  }

  // ********************************************************************************************************
  // UI
  // ********************************************************************************************************

  private createChart(): void {
    if (!this.chart && this.chartDiv?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        const chart = am4core.create('mf-chart__chart_' + this.country.label, am4charts.XYChart);
        this.amChartService.setChartDefaults(chart, true);
        chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm';

        // AXES
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        this.amChartService.setDateAxis(dateAxis, 'date', true, false, true, 80);
        dateAxis.tooltipDateFormat = 'yyyy-MM-dd HH:mm';
        dateAxis.dateFormats.setKey('day', 'MMM dd \'(\'EEE\')\'');
        const valueAxis1 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis1, 'MW');
        const valueAxis2 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis2, '€/MWh');
        valueAxis2.renderer.opposite = true;
        valueAxis2.syncWithAxis = valueAxis1;

        // SERIES
        const seriesPricesFc = chart.series.push(new am4charts.ColumnSeries());
        this.amChartService.setSeries(
          seriesPricesFc,
          'date',
          'power prices forecast',
          'ts',
          'priceFc',
          null,
          this.amChartService.ACCENT,
        );
        seriesPricesFc.yAxis = valueAxis2;
        seriesPricesFc.xAxis = dateAxis;
        seriesPricesFc.fillOpacity = 0.4;
        seriesPricesFc.strokeWidth = 0;

        const seriesResLoad = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesResLoad,
          'date',
          'residual load',
          'ts',
          'resLoad',
          null,
          this.amChartService.PINK_BLUE,
        );
        seriesResLoad.yAxis = valueAxis1;
        seriesResLoad.xAxis = dateAxis;

        const seriesWindFc = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesWindFc,
          'date',
          'wind forecast',
          'ts',
          'windFc',
          null,
          this.amChartService.MAUVE,
        );
        seriesWindFc.yAxis = valueAxis1;
        seriesWindFc.xAxis = dateAxis;

        const seriesPvFc = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesPvFc,
          'date',
          'pv forecast',
          'ts',
          'pvFc',
          null,
          this.amChartService.RED_ORANGE,
        );
        seriesPvFc.yAxis = valueAxis1;
        seriesPvFc.xAxis = dateAxis;

        const seriesCo2Fc = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesCo2Fc,
          'date',
          'co2 forecast',
          'ts',
          'co2Fc',
          null,
          this.amChartService.PINK_ORANGE,
        );
        seriesCo2Fc.yAxis = valueAxis2;
        seriesCo2Fc.xAxis = dateAxis;

        const seriesCoalFc = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesCoalFc,
          'date',
          'coal forecast',
          'ts',
          'coalFc',
          null,
          this.amChartService.FONT,
        );
        seriesCoalFc.yAxis = valueAxis2;
        seriesCoalFc.xAxis = dateAxis;

        const seriesGasFc = chart.series.push(new am4charts.LineSeries());
        this.amChartService.setSeries(
          seriesGasFc,
          'date',
          'gas forecast',
          'ts',
          'gasFc',
          null,
          this.amChartService.PURSE,
        );
        seriesGasFc.yAxis = valueAxis2;
        seriesGasFc.xAxis = dateAxis;

        this.chart = chart;

      });
    }
  }

}
